import React, { useEffect, useState, useMemo, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useActiveCompany } from '../hooks/useActiveCompany';
import {
  Coins, ArrowUpRight, Layers, Users, FileText, Info, CheckCircle2, ChevronDown, ChevronRight, Calculator,
  Settings2, AlertTriangle, Plus, Trash2, Save, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const fmtINR = (n) => (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

/** Pretty-print a structure head rule in English so users can see the formula at a glance. */
const headRuleText = (h) => {
  const calc = h.calculation_type || h.type;
  if (calc === 'monthly_input') {
    const f = h.mi_field || '(pick field)';
    return `From Monthly Input → ${f}`;
  }
  if (calc === 'ot_formula') {
    const base = (h.of || 'BASIC').toUpperCase();
    const d = h.ot_days_per_month || 26;
    const hr = h.ot_hours_per_day || 8;
    const m = h.ot_multiplier || 2;
    return `${base} ÷ ${d} ÷ ${hr} × ot_hours × ${m}`;
  }
  if (h.fixed_monthly != null) return `Fixed ₹${fmtINR(h.fixed_monthly)} / month`;
  if (calc === 'balancing') return 'Balance amount (Gross – other earnings)';
  if (calc === 'slab') return 'Statutory slab (state-specific)';
  if (calc === 'computed') return 'Auto-computed (TDS on slab)';
  if (h.percentage != null || h.percentage_value != null) {
    const pct = h.percentage ?? h.percentage_value;
    const baseRaw = (h.of || h.percentage_of || 'ctc_monthly').toLowerCase();
    const base = baseRaw === 'ctc_monthly' || baseRaw === 'ctc'
      ? 'monthly CTC'
      : baseRaw === 'gross' ? 'gross' : (h.of || h.percentage_of);
    const cap = h.cap_monthly ? ` (capped at ₹${fmtINR(h.cap_monthly)})` : '';
    return `${pct}% of ${base}${cap}`;
  }
  return '—';
};

/**
 * Apply a single-structure breakdown for a given monthly CTC.
 * Mirrors the backend `_expand_ctc_components` so the preview matches the payroll run.
 */
const computeBreakdown = (struct, monthlyCtc) => {
  if (!struct) return { earnings: [], deductions: [], gross: 0, net: 0 };
  const heads = struct.heads || struct.components || [];
  const resolved = {};
  const earningsList = [];
  const deductionsList = [];
  let balancingCode = null;

  // Pass 1 — flat / % of CTC
  heads.forEach(h => {
    const code = h.code || h.head_code;
    if (!code) return;
    if (h.type === 'balancing') { balancingCode = code; return; }
    let amt;
    if (h.fixed_monthly != null) amt = Number(h.fixed_monthly);
    else if (h.percentage != null && (['ctc_monthly', 'ctc', ''].includes((h.of || '').toLowerCase())))
      amt = (monthlyCtc * Number(h.percentage)) / 100;
    if (amt != null) resolved[code] = amt;
  });
  // Pass 2 — % of other head
  heads.forEach(h => {
    const code = h.code || h.head_code;
    const calc = h.calculation_type || h.type;
    if (!code || resolved[code] != null || ['balancing', 'slab', 'computed', 'monthly_input', 'ot_formula'].includes(calc)) return;
    const base = (h.of || '').toUpperCase();
    if (h.percentage != null && base && resolved[base] != null) {
      let amt = (resolved[base] * Number(h.percentage)) / 100;
      if (h.cap_monthly != null) amt = Math.min(amt, Number(h.cap_monthly));
      resolved[code] = amt;
    }
  });
  // Classify — monthly-input / OT-formula heads are deductions if their MI field is
  // other_deduction, otherwise earnings. Regular heuristic for everything else.
  const isDeduction = (code, h) => {
    const calc = h.calculation_type || h.type;
    if (calc === 'monthly_input') return h.mi_field === 'other_deduction';
    return /^(PF|ESI|PT|TDS|LOAN|ADV|LWF|OTHER_DED)/.test(code);
  };
  heads.forEach(h => {
    const code = h.code || h.head_code;
    if (!code || code === balancingCode) return;
    const calc = h.calculation_type || h.type;
    const amt = resolved[code];
    const isPlaceholder = ['slab', 'computed', 'monthly_input', 'ot_formula'].includes(calc);
    const row = { code, rule: headRuleText(h), amount: amt != null ? Math.round(amt) : null, isSlab: isPlaceholder };
    (isDeduction(code, h) ? deductionsList : earningsList).push(row);
  });
  // Balancing — residual of earnings
  if (balancingCode) {
    const earnSoFar = earningsList.reduce((s, r) => s + (r.amount || 0), 0);
    const residual = Math.max(0, monthlyCtc - earnSoFar);
    earningsList.push({ code: balancingCode, rule: 'Balance amount', amount: Math.round(residual) });
  }
  const gross = earningsList.reduce((s, r) => s + (r.amount || 0), 0);
  const totalDed = deductionsList.reduce((s, r) => s + (r.amount || 0), 0);
  return { earnings: earningsList, deductions: deductionsList, gross, net: gross - totalDed };
};

const SalaryStructures = () => {
  const [selectedCompany] = useActiveCompany();
  const [structures, setStructures] = useState([]);
  const [heads, setHeads] = useState([]);
  const [ctcs, setCtcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [previewCtc, setPreviewCtc] = useState(1200000);
  const [editing, setEditing] = useState(null); // structure object being edited

  const refresh = useCallback(() => {
    if (!selectedCompany) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/payroll/structures?company_id=${selectedCompany}`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/masters/all?company_id=${selectedCompany}`, { withCredentials: true }).catch(() => ({ data: {} })),
      axios.get(`${API_URL}/payroll/employee-ctc?company_id=${selectedCompany}`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]).then(([sRes, mRes, cRes]) => {
      setStructures(sRes.data || []);
      setHeads((mRes.data || {}).salary_heads || []);
      setCtcs(cRes.data || []);
    }).finally(() => setLoading(false));
  }, [selectedCompany]);

  useEffect(() => { refresh(); }, [refresh]);

  // Map head code → display name
  const headName = useMemo(() => {
    const m = {};
    heads.forEach(h => { m[h.code] = h.name; });
    return m;
  }, [heads]);

  const byStructure = structures.reduce((acc, s) => {
    acc[s.code] = ctcs.filter(c => c.structure_code === s.code || c.structure_id === s.id);
    return acc;
  }, {});

  const toggle = (code) => setExpanded(e => ({ ...e, [code]: !e[code] }));

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="salary-structures-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
              <Coins className="w-8 h-8 text-[#0052FF]" /> Salary Structures
              <span className="text-xs bg-amber-100 text-amber-800 font-semibold px-2 py-0.5 rounded uppercase ml-2">Read-only viewer</span>
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              View how each CTC is split across heads. Structures are now managed under Business Controls → Salary Structures (CTC Groups).
            </p>
          </div>
          <Link to="/business-controls?tab=ctc">
            <Button className="bg-[#0052FF] hover:bg-[#0043D1] text-white" data-testid="goto-ctc-groups-btn">
              <Layers className="w-4 h-4 mr-2" /> Manage in Business Controls
              <ArrowUpRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>

        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">This page is now a read-only viewer.</p>
            <p className="mt-1 text-amber-800 leading-relaxed">
              All salary structures are created and edited under <Link to="/business-controls?tab=ctc" className="font-semibold underline">Business Controls → Salary Structures (CTC Groups)</Link>.
              That's where you define salary heads, percentages, auto-balancing rules, perks and effective dates in one place.
              Any group you create there automatically shows up here for the payroll engine to consume.
            </p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold">How does CTC get split into heads?</p>
            <p className="mt-1 text-blue-800 leading-relaxed">
              Each structure defines rules per head — <strong>%</strong> (of monthly CTC or another head), <strong>Fixed</strong>,
              <strong>{' '}Balance</strong> (residual), or <strong>Slab</strong> (statutory). At payroll time, the monthly CTC is run
              through these rules to produce Basic / HRA / PF / TDS etc. Click any structure below to see the exact split for your
              preview CTC.
            </p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Kpi label="Structures" value={structures.length} />
          <Kpi label="Salary Heads" value={heads.length} />
          <Kpi label="Employees on CTC" value={ctcs.length} />
          <Kpi label="Default Structure" value={structures.find(s => s.is_default)?.code || '—'} valueSize="text-lg" />
        </div>

        {/* Preview CTC */}
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="ctc-preview-control">
          <label className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Calculator className="w-4 h-4 text-emerald-600" /> Preview breakdown for an annual CTC of
          </label>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-lg">₹</span>
            <input
              type="number"
              value={previewCtc}
              onChange={(e) => setPreviewCtc(Math.max(0, parseInt(e.target.value || '0', 10)))}
              data-testid="ctc-preview-input"
              className="h-10 w-48 px-3 border border-slate-300 rounded-lg text-lg font-mono focus:outline-none focus:ring-2 focus:ring-[#0052FF]"
            />
            <span className="text-xs text-slate-500">= ₹{fmtINR(previewCtc / 12)} / month</span>
          </div>
        </div>

        {/* Structures with expandable head bifurcation */}
        {loading ? (
          <p className="text-sm text-slate-400 text-center py-8">Loading…</p>
        ) : structures.length === 0 ? (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-8 text-center">
            <p className="text-sm text-slate-700">No salary structures defined.</p>
            <p className="text-xs text-slate-500 mt-1">Go to <Link to="/masters-setup" className="text-[#0052FF] underline">Master Setup</Link> → click <strong>Seed Starter Masters</strong>.</p>
          </div>
        ) : structures.map(s => {
          const breakdown = computeBreakdown(s, previewCtc / 12);
          const assignees = byStructure[s.code]?.length || 0;
          const headCount = (s.heads || s.components || []).length;
          const isEmpty = headCount === 0;
          const isOpen = expanded[s.code] ?? !isEmpty; // empty ones start collapsed; configured ones start open
          return (
            <div key={s.id} className={`bg-white border rounded-xl overflow-hidden ${isEmpty ? 'border-amber-300' : 'border-slate-200'}`} data-testid={`structure-${s.code}`}>
              <div className="w-full flex items-center justify-between p-4 hover:bg-slate-50">
                <button onClick={() => toggle(s.code)} className="flex items-center gap-3 flex-1 text-left" data-testid={`structure-toggle-${s.code}`}>
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  <div className="text-left">
                    <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      <span className="font-mono">{s.code}</span>
                      <span className="text-slate-400">—</span>
                      <span>{s.name}</span>
                      {s.is_default && <span className="px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded text-[10px] font-medium">DEFAULT</span>}
                      {isEmpty && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 rounded text-[10px] font-medium inline-flex items-center gap-1" data-testid={`empty-badge-${s.code}`}>
                          <AlertTriangle className="w-3 h-3" /> NO HEADS CONFIGURED
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {headCount} heads · {assignees} employees currently assigned
                    </p>
                  </div>
                </button>
                <div className="flex items-center gap-3">
                  <div className="text-right text-xs">
                    <p className="text-slate-500">For ₹{fmtINR(previewCtc)} CTC</p>
                    <p className="text-slate-900 font-semibold">Gross ₹{fmtINR(breakdown.gross)} / mo · Net ₹{fmtINR(breakdown.net)}</p>
                  </div>
                  <button
                    onClick={() => setEditing(s)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${isEmpty ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'}`}
                    data-testid={`configure-heads-btn-${s.code}`}
                  >
                    <Settings2 className="w-3.5 h-3.5" /> {isEmpty ? 'Configure Heads' : 'Edit Heads'}
                  </button>
                </div>
              </div>

              {isOpen && isEmpty && (
                <div className="border-t border-amber-200 bg-amber-50/50 px-4 py-5 text-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-amber-900">This structure has no salary heads configured yet.</p>
                  <p className="text-xs text-amber-800 mt-1">
                    Until you add heads (Basic, HRA, PF, etc.), employees on this structure will produce a <strong>blank payroll register</strong> with gross = 0.
                  </p>
                  <button
                    onClick={() => setEditing(s)}
                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-medium"
                    data-testid={`configure-heads-cta-${s.code}`}
                  >
                    <Settings2 className="w-3.5 h-3.5" /> Configure Heads Now
                  </button>
                </div>
              )}

              {isOpen && !isEmpty && (
                <div className="border-t border-slate-100">
                  {/* Earnings table */}
                  <div className="p-4 pb-2">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 mb-2">Earnings</p>
                    <table className="w-full text-sm" data-testid={`earnings-table-${s.code}`}>
                      <thead>
                        <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                          <th className="text-left px-2 py-1.5 font-medium">Code</th>
                          <th className="text-left px-2 py-1.5 font-medium">Name</th>
                          <th className="text-left px-2 py-1.5 font-medium">Rule</th>
                          <th className="text-right px-2 py-1.5 font-medium">Monthly ₹</th>
                        </tr>
                      </thead>
                      <tbody>
                        {breakdown.earnings.map(r => (
                          <tr key={r.code} className="border-b border-slate-50" data-testid={`earning-row-${s.code}-${r.code}`}>
                            <td className="px-2 py-1.5 font-mono text-slate-900 font-medium">{r.code}</td>
                            <td className="px-2 py-1.5 text-slate-700">{headName[r.code] || '—'}</td>
                            <td className="px-2 py-1.5 text-slate-600 text-xs">{r.rule}</td>
                            <td className="px-2 py-1.5 text-right font-mono font-semibold text-emerald-700">{r.amount != null ? fmtINR(r.amount) : 'computed'}</td>
                          </tr>
                        ))}
                        <tr className="bg-emerald-50">
                          <td colSpan={3} className="px-2 py-1.5 text-right font-semibold text-emerald-900">Gross Pay</td>
                          <td className="px-2 py-1.5 text-right font-mono font-bold text-emerald-900">{fmtINR(breakdown.gross)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Deductions table */}
                  <div className="p-4 pt-3">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-rose-700 mb-2">Deductions</p>
                    <table className="w-full text-sm" data-testid={`deductions-table-${s.code}`}>
                      <thead>
                        <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                          <th className="text-left px-2 py-1.5 font-medium">Code</th>
                          <th className="text-left px-2 py-1.5 font-medium">Name</th>
                          <th className="text-left px-2 py-1.5 font-medium">Rule</th>
                          <th className="text-right px-2 py-1.5 font-medium">Monthly ₹</th>
                        </tr>
                      </thead>
                      <tbody>
                        {breakdown.deductions.map(r => (
                          <tr key={r.code} className="border-b border-slate-50" data-testid={`deduction-row-${s.code}-${r.code}`}>
                            <td className="px-2 py-1.5 font-mono text-slate-900 font-medium">{r.code}</td>
                            <td className="px-2 py-1.5 text-slate-700">{headName[r.code] || '—'}</td>
                            <td className="px-2 py-1.5 text-slate-600 text-xs">{r.rule}</td>
                            <td className="px-2 py-1.5 text-right font-mono font-semibold text-rose-700">
                              {r.amount != null ? fmtINR(r.amount) : <span className="text-xs italic text-slate-500">applied at run time</span>}
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-slate-50 border-t border-slate-200">
                          <td colSpan={3} className="px-2 py-1.5 text-right font-semibold text-slate-900">Net Pay (approx.)</td>
                          <td className="px-2 py-1.5 text-right font-mono font-bold text-slate-900">{fmtINR(breakdown.net)}</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="text-[11px] text-slate-500 mt-2">
                      Slab-based heads (PT, TDS) are finalized at payroll run time using the employee's location and declarations. The preview shows their placeholders.
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editing && (
        <HeadsEditorModal
          structure={editing}
          heads={heads}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </MainLayout>
  );
};

const Kpi = ({ label, value, valueSize = 'text-3xl' }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <p className={`${valueSize} font-semibold text-slate-900 font-mono`}>{value}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </div>
);

export default SalaryStructures;

/** Head editor — lets users define percentages / fixed amounts / balancing for a structure.
 * Writes to `heads: []` using the seeded-style schema (code, percentage, of, cap_monthly, type)
 * which the backend `_expand_ctc_components` understands. */
const CALC_TYPES = [
  { value: 'percentage', label: '% of base', hint: 'e.g. 40% of CTC_MONTHLY, or 40% of BASIC' },
  { value: 'fixed', label: 'Fixed amount', hint: 'Flat monthly rupee amount' },
  { value: 'balancing', label: 'Balancing', hint: 'Residual to match CTC (use for Special Allowance)' },
  { value: 'slab', label: 'Slab / Statutory', hint: 'Resolved at payroll run time (PT slabs, TDS)' },
  { value: 'monthly_input', label: 'Monthly Input', hint: 'Value pulled from Controls → Monthly Inputs grid for each employee/month' },
  { value: 'ot_formula', label: 'OT Formula', hint: '(Base ÷ days ÷ hours) × OT hours × multiplier. Pulls ot_hours from Monthly Inputs.' },
];

// Monthly Input field catalog shown when calc = monthly_input. Keys match the MonthlyInputRow
// schema in routes_controls.py.
const MI_FIELDS = [
  { value: 'ot_amount',       label: 'OT Amount',        type: 'earning' },
  { value: 'incentive',       label: 'Incentive',        type: 'earning' },
  { value: 'arrears',         label: 'Arrears',          type: 'earning' },
  { value: 'reimbursement',   label: 'Reimbursement',    type: 'earning' },
  { value: 'other_earning',   label: 'Other Earning',    type: 'earning' },
  { value: 'other_deduction', label: 'Other Deduction',  type: 'deduction' },
];

const HeadsEditorModal = ({ structure, heads: allHeads, onClose, onSaved }) => {
  // Normalize incoming heads (either `heads` or `components`) to a common edit-state row.
  const initialRows = () => {
    const src = structure.heads || structure.components || [];
    const guessMi = (code) => {
      const up = (code || '').toUpperCase();
      const map = { OT_AMOUNT: 'ot_amount', OVERTIME: 'ot_amount', OT: 'ot_amount', INCENTIVE: 'incentive',
        ARREAR_INPUT: 'arrears', ARREARS: 'arrears', REIMB_INPUT: 'reimbursement', REIMBURSEMENT: 'reimbursement',
        OTHER_EARN: 'other_earning', OTHER_EARNING: 'other_earning', OTHER_DED: 'other_deduction', OTHER_DEDUCTION: 'other_deduction' };
      return map[up] || up.toLowerCase().replace(/-/g, '_');
    };
    return src.map((h) => {
      const code = h.code || h.head_code || '';
      const rawType = h.type || h.calculation_type || (h.fixed_monthly != null ? 'fixed' : h.percentage != null || h.percentage_value != null ? 'percentage' : 'percentage');
      const type = ['percentage', 'fixed', 'balancing', 'slab', 'computed', 'formula', 'monthly_input', 'ot_formula'].includes(rawType) ? rawType : 'percentage';
      const pct = h.percentage ?? h.percentage_value ?? '';
      const of = h.of || h.percentage_of || 'CTC_MONTHLY';
      const fixed = h.fixed_monthly ?? (h.calculation_type === 'fixed' ? h.value : '') ?? '';
      return {
        code,
        calc: type,
        percentage: pct === '' ? '' : String(pct),
        of: of || 'CTC_MONTHLY',
        cap_monthly: h.cap_monthly != null ? String(h.cap_monthly) : '',
        fixed_monthly: fixed === '' ? '' : String(fixed),
        mi_field: h.mi_field || (type === 'monthly_input' ? guessMi(code) : ''),
        ot_multiplier: h.ot_multiplier != null ? String(h.ot_multiplier) : '2',
        ot_days_per_month: h.ot_days_per_month != null ? String(h.ot_days_per_month) : '26',
        ot_hours_per_day: h.ot_hours_per_day != null ? String(h.ot_hours_per_day) : '8',
      };
    });
  };

  const [rows, setRows] = useState(initialRows);
  const [saving, setSaving] = useState(false);

  const addRow = () => setRows((r) => [...r, {
    code: '', calc: 'percentage', percentage: '', of: 'CTC_MONTHLY', cap_monthly: '', fixed_monthly: '',
    mi_field: '', ot_multiplier: '2', ot_days_per_month: '26', ot_hours_per_day: '8',
  }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  // Base dropdown options = CTC_MONTHLY + any head code already picked as percentage/fixed in earlier rows
  const baseOptions = useMemo(() => {
    const opts = [{ value: 'CTC_MONTHLY', label: 'CTC_MONTHLY' }];
    rows.forEach((r) => {
      if (r.code && !['balancing', 'slab'].includes(r.calc)) {
        opts.push({ value: r.code, label: r.code });
      }
    });
    return opts;
  }, [rows]);

  const usedCodes = new Set(rows.map((r) => r.code).filter(Boolean));
  const availableHeads = allHeads.filter((h) => h.code);

  // Validate & convert rows into the backend schema
  const buildHeadsPayload = () => {
    const out = [];
    for (const r of rows) {
      if (!r.code) continue;
      if (r.calc === 'percentage') {
        const pct = parseFloat(r.percentage);
        if (isNaN(pct) || pct < 0) throw new Error(`${r.code}: enter a valid percentage`);
        const head = { code: r.code, percentage: pct, of: r.of || 'ctc_monthly' };
        if (r.cap_monthly) head.cap_monthly = parseFloat(r.cap_monthly);
        out.push(head);
      } else if (r.calc === 'fixed') {
        const amt = parseFloat(r.fixed_monthly);
        if (isNaN(amt) || amt < 0) throw new Error(`${r.code}: enter a valid fixed amount`);
        out.push({ code: r.code, fixed_monthly: amt });
      } else if (r.calc === 'balancing') {
        out.push({ code: r.code, type: 'balancing' });
      } else if (r.calc === 'slab') {
        out.push({ code: r.code, type: 'slab' });
      } else if (r.calc === 'monthly_input') {
        if (!r.mi_field) throw new Error(`${r.code}: pick a Monthly Input field`);
        out.push({ code: r.code, type: 'monthly_input', mi_field: r.mi_field });
      } else if (r.calc === 'ot_formula') {
        const mult = parseFloat(r.ot_multiplier);
        if (isNaN(mult) || mult <= 0) throw new Error(`${r.code}: enter a valid OT multiplier`);
        out.push({
          code: r.code, type: 'ot_formula',
          of: (r.of || 'BASIC').toUpperCase(),
          ot_multiplier: mult,
          ot_days_per_month: parseFloat(r.ot_days_per_month) || 26,
          ot_hours_per_day: parseFloat(r.ot_hours_per_day) || 8,
        });
      }
    }
    return out;
  };

  const handleSave = async () => {
    try {
      const payload = { heads: buildHeadsPayload() };
      if (payload.heads.length === 0) {
        toast.error('Add at least one head before saving.');
        return;
      }
      setSaving(true);
      await axios.patch(`${API_URL}/payroll/structures/${structure.id}`, payload, { withCredentials: true });
      toast.success(`Saved ${payload.heads.length} heads on ${structure.code}. Payroll register will now show the breakup.`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="heads-editor-modal" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-[#0052FF]" /> Configure Heads — <span className="font-mono text-[#0052FF]">{structure.code}</span>
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">{structure.name}. Each head decides how monthly CTC is split into Basic / HRA / PF / etc.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="editor-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-auto p-5 space-y-3">
          {availableHeads.length === 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-900 flex gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                No Salary Heads defined for this company yet. Add heads first in{' '}
                <Link to="/masters?tab=payroll" className="underline font-semibold">Masters → Salary Heads</Link>{' '}
                (e.g. BASIC, HRA, SPECIAL, PF_EMP) before configuring this structure.
              </div>
            </div>
          )}

          <table className="w-full text-sm" data-testid="heads-editor-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left px-2 py-2 font-medium">Head</th>
                <th className="text-left px-2 py-2 font-medium">Rule</th>
                <th className="text-left px-2 py-2 font-medium">Details</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const dup = row.code && usedCodes.has(row.code) && rows.filter((r) => r.code === row.code).length > 1;
                return (
                  <tr key={i} className="border-b border-slate-100" data-testid={`head-row-${i}`}>
                    <td className="px-2 py-2 align-top">
                      <select
                        value={row.code}
                        onChange={(e) => {
                          const newCode = e.target.value;
                          const up = (newCode || '').toUpperCase();
                          // Auto-detect canonical Monthly Input codes so users don't have
                          // to remember to switch calc type. User can still override.
                          const miMap = { OT_AMOUNT: 'ot_amount', OVERTIME: 'ot_amount', OT: 'ot_amount',
                            INCENTIVE: 'incentive', ARREAR_INPUT: 'arrears', ARREARS: 'arrears',
                            REIMB_INPUT: 'reimbursement', REIMBURSEMENT: 'reimbursement',
                            OTHER_EARN: 'other_earning', OTHER_EARNING: 'other_earning',
                            OTHER_DED: 'other_deduction', OTHER_DEDUCTION: 'other_deduction' };
                          const patch = { code: newCode };
                          if (miMap[up] && (row.calc === 'percentage' || row.calc === 'fixed') && !row.percentage && !row.fixed_monthly) {
                            patch.calc = 'monthly_input';
                            patch.mi_field = miMap[up];
                          }
                          updateRow(i, patch);
                        }}
                        className={`w-full h-9 px-2 border rounded text-sm font-mono ${dup ? 'border-rose-400 bg-rose-50' : 'border-slate-300'}`}
                        data-testid={`head-code-${i}`}
                      >
                        <option value="">— pick head —</option>
                        {availableHeads.map((h) => (
                          <option key={h.code} value={h.code}>{`${h.code} · ${h.name}`}</option>
                        ))}
                      </select>
                      {dup && <p className="text-[10px] text-rose-600 mt-0.5">Duplicate — each head only once.</p>}
                    </td>
                    <td className="px-2 py-2 align-top">
                      <select
                        value={row.calc}
                        onChange={(e) => updateRow(i, { calc: e.target.value })}
                        className="w-full h-9 px-2 border border-slate-300 rounded text-sm"
                        data-testid={`head-calc-${i}`}
                      >
                        {CALC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">{CALC_TYPES.find((t) => t.value === row.calc)?.hint}</p>
                    </td>
                    <td className="px-2 py-2 align-top">
                      {row.calc === 'percentage' && (
                        <div className="flex items-center gap-1.5">
                          <input type="number" step="0.01" value={row.percentage}
                            onChange={(e) => updateRow(i, { percentage: e.target.value })}
                            placeholder="40" className="h-9 w-20 px-2 border border-slate-300 rounded text-sm text-right font-mono"
                            data-testid={`head-pct-${i}`} />
                          <span className="text-sm text-slate-500">% of</span>
                          <select value={row.of}
                            onChange={(e) => updateRow(i, { of: e.target.value })}
                            className="h-9 px-2 border border-slate-300 rounded text-sm font-mono"
                            data-testid={`head-of-${i}`}>
                            {baseOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          <span className="text-xs text-slate-500 ml-2">Cap ₹</span>
                          <input type="number" value={row.cap_monthly}
                            onChange={(e) => updateRow(i, { cap_monthly: e.target.value })}
                            placeholder="optional" className="h-9 w-24 px-2 border border-slate-300 rounded text-sm text-right font-mono"
                            data-testid={`head-cap-${i}`} />
                        </div>
                      )}
                      {row.calc === 'fixed' && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm text-slate-500">₹</span>
                          <input type="number" value={row.fixed_monthly}
                            onChange={(e) => updateRow(i, { fixed_monthly: e.target.value })}
                            placeholder="e.g. 1800" className="h-9 w-32 px-2 border border-slate-300 rounded text-sm text-right font-mono"
                            data-testid={`head-fixed-${i}`} />
                          <span className="text-xs text-slate-500">/ month</span>
                        </div>
                      )}
                      {row.calc === 'balancing' && (
                        <p className="text-xs text-slate-500 italic leading-relaxed">
                          Residual = monthly CTC − (sum of other earnings). Typically applied to Special Allowance so the Gross matches the CTC exactly.
                        </p>
                      )}
                      {row.calc === 'slab' && (
                        <p className="text-xs text-slate-500 italic leading-relaxed">
                          Resolved at run time from the statutory config (PT slab by state, TDS by declaration). No value needed here.
                        </p>
                      )}
                      {row.calc === 'monthly_input' && (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-slate-600">Pull from</span>
                            <select value={row.mi_field}
                              onChange={(e) => updateRow(i, { mi_field: e.target.value })}
                              className="h-9 px-2 border border-slate-300 rounded text-sm"
                              data-testid={`head-mifield-${i}`}>
                              <option value="">— pick field —</option>
                              {MI_FIELDS.map((f) => <option key={f.value} value={f.value}>{`${f.label} (${f.type})`}</option>)}
                            </select>
                            <span className="text-[11px] text-slate-500 ml-1">grid column</span>
                          </div>
                          <p className="text-[10px] text-slate-500 leading-snug">
                            Value is read from <strong>Controls → Monthly Inputs</strong> for each employee/month. If you re-use the canonical MI code (e.g. OT_AMOUNT, INCENTIVE, OTHER_DED), the merge auto-deduplicates.
                          </p>
                        </div>
                      )}
                      {row.calc === 'ot_formula' && (
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs text-slate-600">Base</span>
                            <select value={row.of}
                              onChange={(e) => updateRow(i, { of: e.target.value })}
                              className="h-9 px-2 border border-slate-300 rounded text-sm font-mono"
                              data-testid={`head-otbase-${i}`}>
                              {baseOptions.filter((o) => o.value !== 'CTC_MONTHLY').map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            <span className="text-xs text-slate-500">÷</span>
                            <input type="number" value={row.ot_days_per_month}
                              onChange={(e) => updateRow(i, { ot_days_per_month: e.target.value })}
                              className="h-9 w-14 px-2 border border-slate-300 rounded text-sm text-right font-mono"
                              data-testid={`head-otdays-${i}`} />
                            <span className="text-xs text-slate-500">days ÷</span>
                            <input type="number" value={row.ot_hours_per_day}
                              onChange={(e) => updateRow(i, { ot_hours_per_day: e.target.value })}
                              className="h-9 w-14 px-2 border border-slate-300 rounded text-sm text-right font-mono"
                              data-testid={`head-othours-${i}`} />
                            <span className="text-xs text-slate-500">hrs × OT_hours ×</span>
                            <input type="number" step="0.1" value={row.ot_multiplier}
                              onChange={(e) => updateRow(i, { ot_multiplier: e.target.value })}
                              className="h-9 w-14 px-2 border border-slate-300 rounded text-sm text-right font-mono"
                              data-testid={`head-otmult-${i}`} />
                            <span className="text-xs text-slate-500">×</span>
                          </div>
                          <p className="text-[10px] text-slate-500 leading-snug">
                            Hourly rate = <span className="font-mono">{row.of} ÷ ({row.ot_days_per_month} × {row.ot_hours_per_day})</span>. OT hours come from the Monthly Inputs <span className="font-mono">ot_hours</span> column. Default multiplier <span className="font-mono">2x</span>.
                          </p>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 align-top">
                      <button onClick={() => removeRow(i)} className="text-rose-500 hover:text-rose-700 p-1.5" data-testid={`head-remove-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-sm text-slate-400">
                    No heads yet. Click <strong>Add Head</strong> below to start building the structure.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <button
            onClick={addRow}
            disabled={availableHeads.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium border border-slate-200 disabled:opacity-50"
            data-testid="add-head-btn"
          >
            <Plus className="w-4 h-4" /> Add Head
          </button>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 mt-3">
            <p className="font-semibold flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Quick examples</p>
            <ul className="mt-1 space-y-0.5 list-disc list-inside text-blue-800">
              <li><strong>BASIC</strong> → 40% of CTC_MONTHLY</li>
              <li><strong>HRA</strong> → 50% of BASIC</li>
              <li><strong>SPECIAL</strong> → Balancing (auto-fills the rest)</li>
              <li><strong>PF_EMP</strong> → 12% of BASIC, cap ₹1,800</li>
              <li><strong>PT / TDS</strong> → Slab (resolved at run time)</li>
              <li><strong>OT_AMOUNT / INCENTIVE / OTHER_DED</strong> → <em>Monthly Input</em> (value from Controls → Monthly Inputs)</li>
              <li><strong>OVERTIME (auto)</strong> → <em>OT Formula</em>: BASIC ÷ 26 ÷ 8 × ot_hours × 2</li>
            </ul>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || rows.length === 0}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#0052FF] hover:bg-[#0043D1] text-white rounded-lg text-sm font-medium disabled:opacity-50"
            data-testid="editor-save-btn"
          >
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : `Save ${rows.length} Head${rows.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
};
